import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Bell, Building2, Send, X } from "lucide-react";
import { Mark } from "../brand/Logo";
import { Eyebrow, MaskLines, Reveal } from "../motion/Reveal";
import { useSite } from "../../lib/site";

const PROSPECTS = [
  ["Panadería", "Agente de IA para pedidos", "Presentación enviada", "bg-sun/20 text-[#7a5b00]"],
  ["Clínica dental", "Captación de pacientes", "Interesado", "bg-azure/15 text-[#005a85]"],
  ["Taller mecánico", "Automatización de citas", "Contrató", "bg-leaf/25 text-leaf-deep"],
];

const Phone = ({ appName }) => (
  <div className="relative mx-auto h-[560px] w-[280px] rounded-[2.8rem] border-[10px] border-[#121a3b] bg-[#121a3b] shadow-[0_50px_80px_-30px_rgba(10,18,48,.6)]" data-testid="phone-mockup">
    <div className="absolute left-1/2 top-2 z-10 h-5 w-24 -translate-x-1/2 rounded-full bg-[#121a3b]" />
    <div className="flex h-full flex-col overflow-hidden rounded-[2.1rem] bg-paper">
      <div className="bg-ink px-5 pb-6 pt-10 text-white">
        <div className="flex items-center gap-2">
          <Mark className="h-6 w-6" />
          <span className="font-display text-base font-semibold">{appName}</span>
        </div>
        <p className="mt-4 font-mono text-xs uppercase tracking-[0.12em] text-[#AEB7D3]">Prospectos de hoy</p>
        <p className="font-display text-3xl font-semibold">3 negocios</p>
      </div>
      <div className="flex-1 space-y-2.5 p-4">
        {PROSPECTS.map(([name, need, status, cls], i) => (
          <motion.div
            key={name}
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ delay: 0.3 + i * 0.15, duration: 0.6 }}
            className="rounded-2xl bg-white p-3.5 shadow-sm"
          >
            <div className="flex items-center gap-2.5">
              <span className="grid h-8 w-8 place-items-center rounded-xl bg-ink/5">
                <Building2 className="h-4 w-4 text-ink" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-ink">{name}</p>
                <p className="truncate text-xs text-ink/70">{need}</p>
              </div>
            </div>
            <span className={`mt-2.5 inline-block rounded-full px-2.5 py-1 text-xs font-medium ${cls}`}>{status}</span>
          </motion.div>
        ))}
        <button className="mt-1 flex w-full items-center justify-center gap-2 rounded-2xl bg-leaf py-3 text-sm font-semibold text-ink" tabIndex={-1} aria-hidden="true">
          <Send className="h-4 w-4" /> Compartir presentación
        </button>
      </div>
    </div>
    <div className="absolute -right-6 top-[46%] flex animate-floaty items-center gap-2 rounded-2xl border border-ink/10 bg-white px-3.5 py-2.5 shadow-xl sm:-right-24">
      <Bell className="h-4 w-4 text-leaf-deep" />
      <span className="text-xs font-semibold text-ink">Nuevo cliente interesado</span>
    </div>
  </div>
);

export const MobileTool = () => {
  const { content } = useSite();
  const c = content.celular;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [60, -60]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-paper py-24 sm:py-32 lg:py-40" data-testid="celular-section">
      <div className="container-x grid items-center gap-16 lg:grid-cols-12">
        <div className="relative order-2 lg:order-1 lg:col-span-6">
          <motion.div style={{ y }} className="spotlight absolute inset-x-6 top-10 bottom-10 overflow-hidden rounded-[3rem_10rem_3rem_3rem] sm:inset-x-16">
            <img src={content.media.celular_image} alt="Emprendedor usando su celular" className="h-full w-full object-cover" />
          </motion.div>
          <div className="relative py-6">
            <Phone appName={c.app_name} />
          </div>
          <p className="mt-4 text-center font-mono text-xs uppercase tracking-[0.12em] text-ink/60">Vista ilustrativa de la app del distribuidor</p>
        </div>
        <div className="order-1 lg:order-2 lg:col-span-6">
          <Eyebrow tone="light">{c.eyebrow}</Eyebrow>
          <MaskLines lines={c.title_lines} className="h-section mt-6" testId="celular-title" />
          <ul className="mt-10 space-y-3">
            {c.nots.map((n, i) => (
              <Reveal as="li" key={i} delay={i * 0.06} className="flex items-center gap-4 rounded-2xl border border-ink/10 bg-white px-5 py-4 text-base font-medium sm:text-lg">
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-coral/10 text-coral">
                  <X className="h-4 w-4" strokeWidth={2.6} />
                </span>
                {n}
              </Reveal>
            ))}
          </ul>
          <Reveal className="mt-8 text-lg leading-relaxed text-ink/80">
            <p>{c.text}</p>
          </Reveal>
          <Reveal className="mt-10 font-display text-3xl font-semibold leading-tight tracking-[-0.03em] sm:text-4xl" data-testid="celular-closing">
            <p>{c.closing_1}</p>
            <p className="text-leaf-deep">{c.closing_2}</p>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
